import React, { useRef } from "react";
import { WorkCharge, WorkshopSpecialty } from "../../types/garage/atelier";
import { JOB_STATUSES, useAtelierStore } from "@store/atelierStore";

const SPECIALTY_COLORS: Record<WorkshopSpecialty, { bg: string; border: string; color: string }> = {
  mecanique: { bg: "#EEF0FF", border: "#A5B4FC", color: "#4F46E5" },
  carrosserie: { bg: "#F3EEFF", border: "#C4A8FF", color: "#7C3AED" },
  peinture: { bg: "#FEE2E2", border: "#FECACA", color: "#DC2626" },
  diagnostique: { bg: "#D1FAE5", border: "#A7F3D0", color: "#059669" },
  autre: { bg: "#F5F5F5", border: "#D9D9D9", color: "#737373" },
};

// --- Status Legend ---

export function StatusLegend() {
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span
        className="text-h4 font-medium"
        style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
      >
        Statuts
      </span>
      {JOB_STATUSES.map((s) => (
        <div
          key={s.id}
          className="flex flex-row items-center"
          style={{ gap: 8 }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: s.bgColor,
              border: `1px solid ${s.borderColor}`,
            }}
          />
          <span
            className="text-h4 font-medium"
            style={{ fontFamily: "Poppins", color: "#737373" }}
          >
            {s.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// --- Charge Card ---

export function ChargeCard({
  charge,
  onSplitClick,
  onDragStart,
  isMergeTarget,
}: {
  charge: WorkCharge;
  onSplitClick: (c: WorkCharge) => void;
  onDragStart: (e: React.PointerEvent, c: WorkCharge) => void;
  isMergeTarget?: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const isDragging = useRef(false);
  const setHoveredChargeId = useAtelierStore((s) => s.setHoveredChargeId);

  const sc = SPECIALTY_COLORS[charge.specialty] ?? SPECIALTY_COLORS.autre;

  return (
    <div
      ref={cardRef}
      data-charge-id={charge.id}
      data-charge-ordre={charge.ordre_id ?? ""}
      className="flex flex-row items-center justify-between shadow-sm"
      style={{
        background: isMergeTarget ? "#C7D2FE" : sc.bg,
        border: isMergeTarget
          ? "2px solid #6366F1"
          : charge.autoGenerated
            ? `2px dashed ${sc.border}`
            : `1px solid ${sc.border}`,
        borderRadius: 6,
        padding: "6px 8px",
        cursor: "grab",
        userSelect: "none",
        touchAction: "none",
        opacity: charge.autoGenerated ? 0.85 : 1,
        transform: isMergeTarget ? "scale(1.03)" : undefined,
        transition: "all 0.15s",
      }}
      onMouseEnter={() => setHoveredChargeId(charge.id)}
      onMouseLeave={() => setHoveredChargeId(null)}
      onPointerDown={(e) => {
        pointerStartRef.current = { x: e.clientX, y: e.clientY };
        isDragging.current = false;
      }}
      onPointerMove={(e) => {
        if (!pointerStartRef.current) return;
        if (
          Math.abs(e.clientX - pointerStartRef.current.x) > 4 ||
          Math.abs(e.clientY - pointerStartRef.current.y) > 4
        ) {
          if (!isDragging.current) {
            isDragging.current = true;
            // Capture pointer on the card div itself (not e.target which may be a child span)
            if (cardRef.current) {
              cardRef.current.setPointerCapture(e.pointerId);
            }
            onDragStart(e, charge);
          }
        }
      }}
      onPointerUp={(e) => {
        if (isDragging.current && cardRef.current) {
          cardRef.current.releasePointerCapture(e.pointerId);
        }
        if (!isDragging.current && pointerStartRef.current)
          onSplitClick(charge);
        pointerStartRef.current = null;
        isDragging.current = false;
      }}
    >
      <span
        className="text-h4 font-medium"
        style={{
          fontFamily: "Poppins",
          color: sc.color,
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {charge.clientName}
      </span>
      {charge.autoGenerated && (
        <span
          style={{
            fontSize: 9,
            fontWeight: 600,
            color: "#FFF",
            background: "#6366F1",
            borderRadius: 3,
            padding: "0 3px",
            lineHeight: "14px",
            flexShrink: 0,
            marginLeft: 4,
          }}
        >
          Auto
        </span>
      )}
      <span
        className="text-h4 font-semibold"
        style={{ fontFamily: "Poppins", color: sc.color, opacity: 0.6, marginLeft: 4 }}
      >
        ({charge.hours}h)
      </span>
    </div>
  );
}

// --- Charge Section ---

export function ChargeSection({
  title,
  status,
  charges,
  onSplitClick,
  onChargeDragStart,
  isDropTarget,
  mergeTargetChargeId,
}: {
  title: string;
  status: WorkCharge["status"];
  charges: WorkCharge[];
  onSplitClick: (c: WorkCharge) => void;
  onChargeDragStart: (e: React.PointerEvent, c: WorkCharge) => void;
  isDropTarget?: boolean;
  mergeTargetChargeId?: string | null;
}) {
  return (
    <div
      data-charge-section={status}
      className="flex flex-col"
      style={{
        gap: 6,
        padding: isDropTarget ? "8px 6px" : undefined,
        border: isDropTarget ? "2px dashed #6366F1" : "2px dashed transparent",
        borderRadius: 8,
        background: isDropTarget ? "#EEF0FF" : undefined,
        transition: "all 0.15s",
      }}
    >
      <span
        className="text-h4 font-medium"
        style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
      >
        {title}
      </span>
      {charges.map((c) => (
        <ChargeCard
          key={c.id}
          charge={c}
          onSplitClick={onSplitClick}
          onDragStart={onChargeDragStart}
          isMergeTarget={mergeTargetChargeId === c.id}
        />
      ))}
      {charges.length === 0 && (
        <span
          className="text-h4 font-medium"
          style={{
            fontFamily: "Poppins",
            color: "#D9D9D9",
            fontStyle: "italic",
          }}
        >
          Aucune charge
        </span>
      )}
    </div>
  );
}

// --- Drop zone indicator for returning jobs to charges ---

export function ChargeDropZone({ active }: { active: boolean }) {
  return (
    <div
      className="flex items-center justify-center"
      style={{
        border: `2px dashed ${active ? "#6366F1" : "#D9D9D9"}`,
        borderRadius: 8,
        padding: "12px 8px",
        background: active ? "#EEF0FF" : "#FAFAFA",
        transition: "all 0.15s",
        marginTop: 8,
      }}
    >
      <span
        className="text-h4 font-medium"
        style={{
          fontFamily: "Poppins",
          color: active ? "#6366F1" : "#A1A1A1",
          textAlign: "center",
        }}
      >
        {active
          ? "Relâcher pour renvoyer en charge"
          : "Glisser un RDV ici pour le retirer"}
      </span>
    </div>
  );
}
